import { ReactElement, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AcUnit, Air, Check, DoDisturb } from '@mui/icons-material'
import ListAltIcon from '@mui/icons-material/ListAlt'
import { Box } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { LeftRailNav } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useEditorNavigation, useIsAnyActionBlocked } from '@editorHooks'
import { getResultsValid, getWindLoadsResults, type ResultsValidityResponse } from '@queries'
import { renderNavIcon } from './components/renderNavIcon'
import { Drawer } from './styles'

/** Shown when switching pages is blocked because a form (design, Zugverbindertabelle) is unsaved. */
const UNSAVED_CHANGES_NAV_MESSAGE =
  'Bitte speichern Sie Ihre Änderungen, bevor Sie die Seite wechseln.'

const unsavedChangesNavSnackbarOptions = {
  variant: 'warning' as const,
  preventDuplicate: true,
  key: 'process-navigation-unsaved-changes',
}

interface Props {
  transparent?: boolean
}

const ProcessNavigation = ({ transparent = true }: Props): ReactElement => {
  const { currentRouteIndex, currentConfig } = useEditorNavigation()
  const navigate = useNavigate()

  const { enqueueSnackbar } = useSnackbar()
  const { isBlockedFn: isAnyActionBlockedFn } = useIsAnyActionBlocked()

  // Switching pages while any form (e.g. Bemessung, Zugverbindertabelle) has unsaved changes is
  // blocked; the offending page keeps its own "please save" affordances.
  const guardedNavigate = useCallback(
    (to: string) => {
      if (isAnyActionBlockedFn()) {
        enqueueSnackbar(UNSAVED_CHANGES_NAV_MESSAGE, unsavedChangesNavSnackbarOptions)
        return
      }
      navigate(to)
    },
    [isAnyActionBlockedFn, enqueueSnackbar, navigate],
  )

  const project = useModelStore(state => state.project) as ProjectWithLoadInfo | undefined
  const projectId = project?.guid

  const { data: windLoadsResults } = useQuery<WindloadResults | null>({
    queryKey: getWindLoadsResults.getKey(projectId ?? ''),
    queryFn: () => getWindLoadsResults.request(projectId!),
    enabled: Boolean(projectId),
  })

  const { data: resultsValid, isLoading: isLoadingResultsValid } =
    useQuery<ResultsValidityResponse | null>({
      queryKey: getResultsValid.getKey(projectId ?? ''),
      queryFn: () => getResultsValid.request(projectId!),
      enabled: Boolean(projectId),
      refetchInterval: 5000,
    })

  const isWindloadsResetted = useMemo(() => {
    const data = windLoadsResults
    if (!data) return false
    const isForceResetted = (force: Vector) =>
      (force.x === 10 && force.y === 0) ||
      (force.x === 0 && force.y === 10) ||
      (force.x === -10 && force.y === 0) ||
      (force.x === 0 && force.y === -10)

    let isResetted = true
    Object.values(data.wind_loads_per_ceiling).forEach(ceilings => {
      ceilings.forEach(ceiling => {
        if (!isForceResetted(ceiling.force)) isResetted = false
      })
    })
    Object.values(data.wind_loads_roof).forEach(results => {
      results.forEach(result => {
        if (!isForceResetted(result.force)) isResetted = false
      })
    })
    return isResetted
  }, [windLoadsResults])

  const resultsState = useMemo<
    'loading' | 'missing' | 'partially_valid' | 'fully_valid' | 'invalid'
  >(() => {
    if (isLoadingResultsValid) return 'loading'
    if (resultsValid === null || resultsValid === undefined) return 'missing'
    if (resultsValid.fully_valid) return 'fully_valid'
    if (resultsValid.partially_valid) return 'partially_valid'
    return 'invalid'
  }, [isLoadingResultsValid, resultsValid])

  const items = useMemo(() => {
    const loadInfo = project?.load_info
    const hasWind = Boolean(loadInfo?.has_wind_loads) && !isWindloadsResetted
    const hasSnow = Boolean(loadInfo?.has_snow_loads)

    return currentConfig.map(({ route, label, icon }) => {
      if (route.endsWith('global-loads')) {
        return {
          id: route,
          label,
          icon: renderNavIcon(icon),
          subItems: [
            {
              id: 'wind',
              title: 'Windlasten',
              tooltip: hasWind ? 'Windlasten konfiguriert' : 'Windlasten nicht konfiguriert',
              icon: (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 16,
                    height: 16,
                    '@media (min-width: 1920px)': { width: 18, height: 18 },
                  }}
                >
                  <Air sx={{ fontSize: 14, '@media (min-width: 1920px)': { fontSize: 16 } }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -3,
                      bottom: -3,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: 9 },
                      '@media (min-width: 1920px)': {
                        width: 11,
                        height: 11,
                        '& svg': { fontSize: 10 },
                      },
                    }}
                  >
                    {hasWind ? (
                      <Check data-cy="project-state-wind-loads-configured-icon" />
                    ) : (
                      <DoDisturb data-cy="project-state-wind-loads-not-configured-icon" />
                    )}
                  </Box>
                </Box>
              ),
              color: hasWind ? '#2E7D32' : '#ED6C02',
              onClick: () => guardedNavigate(`${route}?tab=wind-loads`),
            },
            {
              id: 'snow',
              title: 'Schneelasten',
              tooltip: hasSnow ? 'Schneelasten konfiguriert' : 'Schneelasten nicht konfiguriert',
              icon: (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 16,
                    height: 16,
                    '@media (min-width: 1920px)': { width: 18, height: 18 },
                  }}
                >
                  <AcUnit sx={{ fontSize: 14, '@media (min-width: 1920px)': { fontSize: 16 } }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -3,
                      bottom: -3,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: 9 },
                      '@media (min-width: 1920px)': {
                        width: 11,
                        height: 11,
                        '& svg': { fontSize: 10 },
                      },
                    }}
                  >
                    {hasSnow ? (
                      <Check data-cy="project-state-snow-loads-configured-icon" />
                    ) : (
                      <DoDisturb data-cy="project-state-snow-loads-not-configured-icon" />
                    )}
                  </Box>
                </Box>
              ),
              color: hasSnow ? '#2E7D32' : '#ED6C02',
              onClick: () => guardedNavigate(`${route}?tab=snow-loads`),
            },
          ],
        }
      }
      if (route.endsWith('results')) {
        let resultsTitle = ''
        switch (resultsState) {
          case 'fully_valid':
            resultsTitle = 'Ergebnisse gültig'
            break
          case 'partially_valid':
            resultsTitle = 'Ergebnisse vorhanden, aber Neu-Berechnung notwendig'
            break
          case 'invalid':
            resultsTitle = 'Ergebnisse ungültig'
            break
          case 'missing':
            resultsTitle = 'Keine Ergebnisse berechnet'
            break
          case 'loading':
            resultsTitle = 'Ergebnisse werden geprüft…'
        }

        let resultsColor = ''
        switch (resultsState) {
          case 'fully_valid':
            resultsColor = '#2E7D32'
            break
          case 'partially_valid':
            resultsColor = '#FFC107'
            break
          case 'invalid':
            resultsColor = '#D32F2F'
            break
          case 'missing':
            resultsColor = '#ED6C02'
            break
          case 'loading':
            resultsColor = 'rgba(0,0,0,0.45)'
        }

        let ResultsBadgeIcon: ReactElement | null = null
        switch (resultsState) {
          case 'fully_valid':
            ResultsBadgeIcon = <Check data-cy="project-state-results-fully-valid-configured-icon" />
            break
          case 'partially_valid':
            ResultsBadgeIcon = (
              <Check data-cy="project-state-results-partially-valid-configured-icon" />
            )
            break
          case 'invalid':
            ResultsBadgeIcon = <DoDisturb data-cy="project-state-results-invalid-configured-icon" />
            break
          case 'missing':
            ResultsBadgeIcon = <DoDisturb data-cy="project-state-results-missing-configured-icon" />
            break
        }

        return {
          id: route,
          label,
          icon: renderNavIcon(icon),
          subItems: [
            {
              id: 'results-valid',
              title: resultsTitle,
              tooltip:
                resultsState === 'fully_valid'
                  ? 'Ergebnisse vorhanden'
                  : resultsState === 'partially_valid'
                  ? 'Ergebnisse vorhanden, aber Neu-Berechnung notwendig'
                  : 'Ergebnisse nicht vorhanden',
              icon: (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 16,
                    height: 16,
                    '@media (min-width: 1920px)': { width: 18, height: 18 },
                  }}
                >
                  <ListAltIcon
                    sx={{ fontSize: 14, '@media (min-width: 1920px)': { fontSize: 16 } }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -3,
                      bottom: -3,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: 9 },
                      '@media (min-width: 1920px)': {
                        width: 11,
                        height: 11,
                        '& svg': { fontSize: 10 },
                      },
                    }}
                  >
                    {ResultsBadgeIcon}
                  </Box>
                </Box>
              ),
              color: resultsColor,
              onClick: () => guardedNavigate(route),
            },
          ],
        }
      }
      return {
        id: route,
        label,
        icon: renderNavIcon(icon),
      }
    })
  }, [currentConfig, isWindloadsResetted, guardedNavigate, project?.load_info, resultsState])

  return (
    <Drawer variant="permanent" transparent={transparent}>
      <Box display="flex" flexDirection="column" width="100%" alignItems="center" gap={0.75} px={0}>
        {/* Really small box that can be used as an anchor to any DraggablePopup */}
        <Box width={1} height={1} id="popup-anchor" />
        <LeftRailNav
          items={items}
          value={currentConfig[currentRouteIndex]?.route}
          onChange={id => guardedNavigate(id)}
        />
      </Box>
    </Drawer>
  )
}

export default ProcessNavigation
